import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/types/graph';
import { selectIsFirstStep } from '@/onboarding/orchestration/selectors';
import { useResetQuestionnaire } from '@/hooks/useResetQuestionnaire';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { isKioskOnboarding, KIOSK_INACTIVITY_RESET_EVENT } from '@/util/kiosk';

const KIOSK_IDLE_TIMEOUT_MS = 45_000;
const KIOSK_COUNTDOWN_SECONDS = 20;

/** Activity that re-arms the idle timer. */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'mousemove',
  'keydown',
  'touchstart',
  'scroll',
] as const;

export const KioskInactivityGuard = () => {
  const resetQuestionnaire = useResetQuestionnaire();

  const isKiosk = useSelector((state: RootState) =>
    isKioskOnboarding(state.questionnaire.config?.patientOnboarding),
  );
  const isFirstStep = useSelector(selectIsFirstStep);

  // Guard every kiosk page except the flow's first step — the neutral start
  // screen (the landing page) has nothing to protect, and resetting it would
  // loop right after a reset.
  const enabled = isKiosk && !isFirstStep;

  const [showModal, setShowModal] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(KIOSK_COUNTDOWN_SECONDS);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Read in the activity listener without re-subscribing on every render.
  const showModalRef = useRef(false);
  showModalRef.current = showModal;

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleReset = useCallback(() => {
    clearTimers();
    setShowModal(false);
    window.dispatchEvent(new Event(KIOSK_INACTIVITY_RESET_EVENT));
    resetQuestionnaire();
  }, [clearTimers, resetQuestionnaire]);

  const startCountdown = useCallback(() => {
    setSecondsLeft(KIOSK_COUNTDOWN_SECONDS);
    setShowModal(true);
    countdownRef.current = setInterval(() => {
      // Keep the updater pure — just decrement. The reset is fired from the
      // effect below once this reaches 0, so it can't double-run.
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1_000);
  }, []);

  const armIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = setTimeout(startCountdown, KIOSK_IDLE_TIMEOUT_MS);
  }, [startCountdown]);

  // Fire the reset once the countdown reaches 0
  useEffect(() => {
    if (showModal && secondsLeft === 0) {
      handleReset();
    }
  }, [showModal, secondsLeft, handleReset]);

  const handleStillHere = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setShowModal(false);
    armIdleTimer();
  }, [armIdleTimer]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setShowModal(false);
      return;
    }

    armIdleTimer();

    // Any activity re-arms the idle timer — but once the modal is up, only the
    // explicit "Oui, je suis là" button counts as a response.
    const onActivity = () => {
      if (!showModalRef.current) {
        armIdleTimer();
      }
    };
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true }),
    );

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, onActivity),
      );
      clearTimers();
    };
  }, [enabled, armIdleTimer, clearTimers]);

  if (!showModal) return null;

  return (
    <Dialog open={showModal}>
      <DialogContent className="nsm:w-[452px]">
        <div className="flex flex-col items-center gap-4 px-5 py-4 text-center nsm:px-8">
          <p className="text-xl font-medium">Êtes-vous toujours là ?</p>
          <p className="font-light">
            Sans réponse de votre part, le questionnaire va redémarrer dans{' '}
            <span className="font-medium">{secondsLeft}s</span> pour le prochain
            patient.
          </p>
          <Button
            variant="ctaPrimary"
            size="lg"
            onClick={handleStillHere}
            className="mt-2"
          >
            Oui, je suis là
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
